/**
 * CleverTap analytics for the cart drawer and the drawer upsell slider.
 *
 * Listener-only by design: every handler is passive (no preventDefault, no cart
 * mutation, no interference with GoKwik / cart.js / cart-drawer.js). All work is
 * delegated off `document` so it survives the drawer re-rendering its innerHTML.
 *
 * Event payload follows the theme convention: flat snake_case props spread into
 * the dataLayer push and repeated under a `clevertap` key.
 */
(function () {
  var DRAWER = '#CartDrawer';

  /* ---------------------------------------------------------------- helpers */

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function deviceType() {
    return window.innerWidth < 768 ? 'Mobile' : 'Desktop';
  }

  function cartValue() {
    var el = document.querySelector('.totals__subtotal-value');
    if (!el) return '';
    var numeric = Number(el.getAttribute('data-cartvalue'));
    return isFinite(numeric) ? numeric : '';
  }

  function cartRows() {
    return Array.prototype.slice.call(document.querySelectorAll(DRAWER + ' .cart-item'));
  }

  // total units in cart (matches the "(5 Items)" count), not the line count
  function cartQuantity() {
    return cartRows().reduce(function (total, row) {
      var input = row.querySelector('.quantity__input');
      return total + (input ? Number(input.value) || 0 : 0);
    }, 0);
  }

  function commonData() {
    var drawer = drawerEl();
    return {
      cart_value: cartValue(),
      cart_quantity: cartQuantity(),
      cart_line_count: cartRows().length,
      page_type: (drawer && drawer.getAttribute('data-template-name')) || '',
      page_url: window.location.href,
      device_type: deviceType()
    };
  }

  // CleverTap event properties must be flat scalars — an array or object value
  // gets the property (sometimes the whole event) dropped, so collections are
  // sent as comma-joined strings, matching the existing `Custom_Upsell` payload.
  function flatten(data) {
    var out = {};
    Object.keys(data).forEach(function (key) {
      var value = data[key];
      if (Array.isArray(value)) out[key] = value.join(', ');
      else if (value === null || value === undefined) out[key] = '';
      else out[key] = value;
    });
    return out;
  }

  function track(eventName, props) {
    var clevertapData;

    try {
      clevertapData = flatten(Object.assign({}, commonData(), props || {}));
    } catch (e) {
      console.warn('cart-drawer-analytics:', e);
      return;
    }

    // 1) GTM / GA4 — the theme's existing "dual push" convention
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(
        Object.assign({ event: eventName }, clevertapData, { clevertap: clevertapData })
      );
    } catch (e) {
      console.warn('cart-drawer-analytics:', e);
    }

    // 2) CleverTap directly. The SDK is injected by GTM, not by the theme, so a
    // dataLayer push only reaches CleverTap if a GTM tag+trigger exists for that
    // event name. Pushing here as well is what makes new events land without any
    // GTM change — same approach product-form.js uses for `cart_updated`.
    try {
      if (typeof clevertap !== 'undefined' && clevertap.event) {
        clevertap.event.push(eventName, clevertapData);
      }
    } catch (e) {
      console.warn('cart-drawer-analytics:', e);
    }

    // window.CART_ANALYTICS_DEBUG = true  → log every event in the console
    if (window.CART_ANALYTICS_DEBUG) {
      console.log('[cart-drawer-analytics]', eventName, clevertapData);
    }
  }

  function lineItemData(row) {
    if (!row) return {};
    var input = row.querySelector('.quantity__input');
    return {
      product_id: row.getAttribute('data-product-id') || '',
      variant_id:
        row.getAttribute('data-variant-id') ||
        (input && input.getAttribute('data-quantity-variant-id')) ||
        '',
      product_name: row.getAttribute('data-product-title') || '',
      product_price: row.getAttribute('data-product-price') || '',
      product_handle: row.getAttribute('data-product-handle') || '',
      quantity: input ? Number(input.value) || 0 : 0
    };
  }

  function cartProductNames() {
    return cartRows().map(function (row) {
      return row.getAttribute('data-product-title') || '';
    });
  }

  function cartProductIds() {
    return cartRows().map(function (row) {
      return row.getAttribute('data-product-id') || '';
    });
  }

  function upsellProducts() {
    return Array.prototype.slice
      .call(document.querySelectorAll(DRAWER + ' .custom-upcart-container .btn-span'))
      .map(function (el) {
        return {
          id: el.getAttribute('data-variant-id') || '',
          name: el.getAttribute('data-product-title') || '',
          price: el.getAttribute('data-variant-price') || ''
        };
      });
  }

  /* ------------------------------------------------------ upsell impression */

  var lastUpsellSignature = '';

  function trackUpsellViewed() {
    var wrap = document.querySelector(DRAWER + ' .upsell-main');
    if (!wrap || wrap.classList.contains('hidden')) return;

    var products = upsellProducts();
    if (!products.length) return;

    // the drawer re-renders on every cart change; only report a genuinely new set
    var signature = products
      .map(function (p) {
        return p.id;
      })
      .join(',');
    if (signature === lastUpsellSignature) return;
    lastUpsellSignature = signature;

    track('cart_drawer_upsell_viewed', {
      upsell_product_count: products.length,
      upsell_product_ids: products.map(function (p) {
        return p.id;
      }),
      upsell_product_names: products.map(function (p) {
        return p.name;
      })
    });
  }

  /* ------------------------------------------------------- drawer open/close */

  function reportDrawerOpened() {
    track('cart_drawer_viewed', {
      product_names: cartProductNames(),
      product_ids: cartProductIds()
    });
    trackUpsellViewed();
  }

  function watchDrawer() {
    var drawer = drawerEl();
    if (!drawer) return;

    var wasActive = drawer.classList.contains('active');
    var debounce;

    // the drawer can already be open by the time this runs (?cartOpen=true deep
    // link), which the observer below would otherwise never see as a transition
    if (wasActive) reportDrawerOpened();

    new MutationObserver(function () {
      var isActive = drawer.classList.contains('active');

      if (isActive !== wasActive) {
        wasActive = isActive;
        if (isActive) {
          reportDrawerOpened();
        } else {
          // let the next open report its upsell set again
          lastUpsellSignature = '';
        }
        return;
      }

      // drawer contents re-rendered while open (add/remove/quantity change)
      if (isActive) {
        clearTimeout(debounce);
        debounce = setTimeout(trackUpsellViewed, 300);
      }
    }).observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });
  }

  /* ------------------------------------------------------------- interactions */

  // capture phase: read the row before cart.js re-renders the drawer
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      // remove (trash icon)
      var removeBtn = target.closest(DRAWER + ' cart-remove-button');
      if (removeBtn) {
        track('cart_drawer_item_removed', lineItemData(removeBtn.closest('.cart-item')));
        return;
      }

      // cart line item -> PDP
      var productLink = target.closest(DRAWER + ' .cart-item__name, ' + DRAWER + ' .cart-item__link');
      if (productLink) {
        track(
          'cart_drawer_product_clicked',
          Object.assign(lineItemData(productLink.closest('.cart-item')), {
            destination_url: productLink.getAttribute('href') || ''
          })
        );
        return;
      }

      // upsell slider arrows
      var navBtn = target.closest(DRAWER + ' .custom-upcart-container .slider__nav');
      if (navBtn) {
        track('cart_drawer_upsell_navigated', {
          direction: navBtn.classList.contains('slider__nav--next') ? 'next' : 'previous',
          upsell_product_count: upsellProducts().length
        });
        return;
      }

      // upsell card tapped anywhere other than ADD
      var upsellCard = target.closest(DRAWER + ' .custome-upcart-card');
      if (upsellCard && !target.closest('.btn-span')) {
        var btn = upsellCard.querySelector('.btn-span');
        track('cart_drawer_upsell_product_clicked', {
          product_name: (btn && btn.getAttribute('data-product-title')) || '',
          variant_id: (btn && btn.getAttribute('data-variant-id')) || '',
          product_price: (btn && btn.getAttribute('data-variant-price')) || ''
        });
        return;
      }

      // checkout / PLACE ORDER (GoKwik's own onclick is left untouched)
      var checkoutBtn = target.closest(
        DRAWER + ' .gokwik-checkout button, ' + DRAWER + ' .cart__checkout-button'
      );
      if (checkoutBtn) {
        track('cart_drawer_checkout_clicked', {
          product_names: cartProductNames(),
          product_ids: cartProductIds()
        });
      }
    },
    true
  );

  // Last quantity reported per input. The `value` attribute is only the
  // server-rendered figure, so it goes stale between a change and the drawer
  // re-render; on re-render the input is a fresh element with a correct
  // attribute, so this map self-heals.
  var lastQuantity = new WeakMap();

  // quantity +/- : listen for the value actually changing, so a change blocked by
  // the max-quantity guard in global.js never reports a false increment
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.closest || !input.closest(DRAWER + ' .quantity__input')) return;

    var newQty = Number(input.value) || 0;
    var previousQty = lastQuantity.has(input)
      ? lastQuantity.get(input)
      : Number(input.getAttribute('value')) || 0;

    lastQuantity.set(input, newQty);

    if (newQty === previousQty) return;

    // stepping down to 0 is a removal; cart-remove-button reports its own event
    if (newQty === 0) return;

    track(
      newQty > previousQty ? 'cart_drawer_quantity_increased' : 'cart_drawer_quantity_decreased',
      Object.assign(lineItemData(input.closest('.cart-item')), {
        quantity: newQty,
        previous_quantity: previousQty,
        quantity_change: Math.abs(newQty - previousQty)
      })
    );
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  } else {
    watchDrawer();
  }
})();
